/**
 * Split the candidate's permanent-address string into its four named
 * pieces. The case-detail GET returns the address as a single comma-
 * separated value in the documented order:
 *
 *     "Village (CODE), Block, District, PIN"
 *
 * e.g. `"Athwal (427), Dera Baba Nanak, Gurdaspur, 143511"` parses to
 * `{ village: "Athwal (427)", block: "Dera Baba Nanak",
 *    district: "Gurdaspur", pin: "143511" }`.
 *
 * Empty / null / unparseable input → every field is `null` so callers
 * can render the rows conditionally without juggling string emptiness.
 *
 * Trailing extras beyond the fourth piece are folded into `pin` so we
 * don't silently drop data when the backend formats start to drift.
 * Missing trailing pieces are left as `null`.
 */
export interface ParsedAddress {
  village: string | null;
  block: string | null;
  district: string | null;
  pin: string | null;
}

const EMPTY: ParsedAddress = { village: null, block: null, district: null, pin: null };

export function parseAddress(raw: string | null | undefined): ParsedAddress {
  if (!raw) return EMPTY;
  const parts = raw
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
  if (parts.length === 0) return EMPTY;

  // Pad to four slots with null so destructuring lines up neatly.
  const [village = null, block = null, district = null, ...rest] = parts;
  const pin = rest.length > 0 ? rest.join(', ') : parts[3] ?? null;
  return { village, block, district, pin };
}

/** Convenience: true when the parsed address has no usable pieces. */
export function isEmptyAddress(a: ParsedAddress): boolean {
  return !a.village && !a.block && !a.district && !a.pin;
}
